import {UserAddOutlined} from '@ant-design/icons';
import {Button, Space, Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {
  MasterAccountFieldsFragment,
  SearchableMasterAccountFilterInput,
  useMasterAccountsQuery
} from 'api/generated';
import {Ellipsize, Favicon, TableCard} from 'components';
import deepEqual from 'deep-equal';
import {useTestModeState} from 'hooks/useTestModeState';
import {formatEmail, formatRelativeDate, formatWebsite} from 'lib/formaters';
import {omitEmpty} from 'lib/omitEmpty';
import React from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {FilterQueryParams} from 'routes/Accounts/AccountFilters';
import {NumberParam, StringParam, useQueryParam, useQueryParams} from 'use-query-params';
import {FilterList} from '../../components/FilterList';
import {MASTER_ACCOUNT_FILTERS} from '../../lib/enums';
import {sortObjectKeysByValue, truncate} from '../../lib/utils';
import {MasterAccountFilters} from './MasterAccountFilters';

export const queryFiltersSchema = {
  id: StringParam,
  name: StringParam,
  promoCode: StringParam,
  adminEmail: StringParam
};

const textFields = ['id', 'name', 'promoCode', 'adminEmail'];

const getQueryType = (key: string) => {
  if (textFields.includes(key)) {
    return 'matchPhrase';
  }
  return 'match';
};

const buildFilter = (queryFilters: {[key: string]: any}): SearchableMasterAccountFilterInput => {
  const filter: SearchableMasterAccountFilterInput = {};
  Object.entries(queryFilters).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const queryType = getQueryType(key);
    (filter as any)[key] = {[queryType]: value};
  });
  return filter;
};

export const MasterAccounts: React.FC<RouteComponentProps<{}>> = ({history}) => {
  const [_size, setSize] = useQueryParam('size', NumberParam);
  const [_from, setFrom] = useQueryParam('from', NumberParam);
  const [queryFilters, setQueryFilters] = useQueryParams(queryFiltersSchema);

  const size = _size || 20;
  const from = _from || 0;

  const filter = buildFilter(queryFilters);
  const {data, loading, refetch, previousData} = useMasterAccountsQuery({
    variables: {from, size, filter}
  });

  const payload = data ?? previousData;
  const accounts = payload?.masterAccounts.items || [];
  const total = payload?.masterAccounts.total || undefined;

  const [isTestMode] = useTestModeState();

  const columns: Array<ColumnProps<MasterAccountFieldsFragment>> = [
    {
      key: 'favicon',
      className: 'minimized-cell',
      onCell: (account) => ({
        onClick() {
          history.push(`/master-accounts/${account.id}`);
        }
      }),
      render(account) {
        return <Favicon account={account} shape="square" />;
      }
    },
    {
      title: 'Name',
      dataIndex: 'name',
      onCell: (account) => ({
        onClick() {
          history.push(`/master-accounts/${account.id}`);
        }
      }),
      render(name: string) {
        return <Space>{truncate(name, 35)}</Space>;
      }
    },
    {
      title: 'Email',
      dataIndex: 'adminEmail',
      className: 'minimized-cell',
      render: (email) => (
        <Ellipsize maxWidth={{xl: '90ch', md: '25ch', sm: '15ch'}}>{formatEmail(email)}</Ellipsize>
      )
    },
    {
      title: 'Website',
      dataIndex: 'business',
      className: 'minimized-cell',
      render: (business: any) => {
        if (business && business.website) {
          return (
            <Ellipsize maxWidth={{xl: '90ch', md: '25ch', sm: '15ch'}}>
              {formatWebsite(business.website)}
            </Ellipsize>
          );
        }
      }
    },
    {
      title: 'Promo code',
      dataIndex: 'promoCode',
      className: 'minimized-cell',
      render: (promoCode: string) => {
        return promoCode;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      className: 'minimized-cell',
      render: (plan: string) => {
        return plan ? plan.replace(/_/g, ' ') : null;
      }
    },
    {
      title: 'Last update',
      dataIndex: 'createdAt',
      className: 'minimized-cell',
      defaultSortOrder: 'descend',
      render(_, account) {
        const date = account.updatedAt || account.createdAt;
        if (!date) return null;
        return formatRelativeDate(date);
      }
    }
  ];

  const handleFiltersChange = (values: FilterQueryParams) => {
    if (deepEqual(omitEmpty(values), omitEmpty(queryFilters))) return refetch();
    setQueryFilters(values, 'push');
  };

  const handleCloseTag = (key: string) => setQueryFilters({...queryFilters, [key]: null}, 'push');

  let availableFilters: Record<string, string> = {...MASTER_ACCOUNT_FILTERS};

  availableFilters = sortObjectKeysByValue(availableFilters);

  return (
    <>
      <MasterAccountFilters
        onChange={handleFiltersChange}
        queryFilters={queryFilters}
        availableFilters={availableFilters}
      />
      <FilterList queryFilters={queryFilters} filters={availableFilters} onClose={handleCloseTag} />
      <TableCard
        size="small"
        title="Master Accounts"
        extra={
          !isTestMode && (
            <Button onClick={() => history.push('/master-accounts/new')}>
              <UserAddOutlined /> Create
            </Button>
          )
        }>
        <Table
          scroll={{x: true}}
          dataSource={accounts}
          columns={columns}
          loading={loading}
          rowClassName={() => 'clickable'}
          rowKey="id"
          pagination={{
            pageSize: size,
            current: from / size + 1,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total.toLocaleString()} accounts`,
            onChange: (page, size = 0) => setFrom((page - 1) * size, 'pushIn'),
            onShowSizeChange: (page, size) => setSize(size, 'pushIn')
          }}
        />
      </TableCard>
    </>
  );
};
